import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn } from '@angular/router';
import { AuthorizationModel } from '../models/data/authorization.model';
import { OrganizationStatuses } from '../enums/data.enums';
import { AuthorizationStateService } from '../services/authorization/authorization-state.service';
import { OrganizationService } from '../services/organization.service';
import { UserSessionService } from '../services/user-session.service';

/**
 * Query parameter an email deep link uses to name the organization a page should open in, e.g.
 * `/payment-dashboard?organizationId=12`. Part of the email deep-link contract with the Core API
 * (`DashboardLinkUtilities.cs`) — rename it only together with the API (DEV-2044).
 */
export const ORGANIZATION_QUERY_PARAM = 'organizationId';

/**
 * The organization a deep link asks for, but only when the viewer is a member of it. Anything else —
 * absent, malformed, or an organization the viewer does not belong to — is `undefined`, so the page
 * behaves exactly as it did before the link existed.
 */
function resolveRequestedOrganizationId(
  route: ActivatedRouteSnapshot,
  authorizationState: AuthorizationModel | undefined
): number | undefined {
  const requestedOrganizationId = Number(route.queryParamMap.get(ORGANIZATION_QUERY_PARAM)) || undefined;
  if (requestedOrganizationId === undefined) return undefined;

  const isMember = authorizationState?.memberPermissions?.some(mp => mp.organizationId === requestedOrganizationId);
  return isMember ? requestedOrganizationId : undefined;
}

/**
 * Makes the organization named by `?organizationId` the active one before an organization-scoped page
 * loads, mirroring a manual pick in the organization switcher. Never blocks: when the organization cannot
 * be resolved or loaded, the active organization is left as it was.
 *
 * Put it on a componentless PARENT of the page's route, never beside the page's own guards. Guards on one
 * route run concurrently, but a parent's guards finish before its children's start, so the child's
 * permission and organization checks then see the organization that is actually active — including when
 * this switch fails.
 */
export const organizationQueryParamGuard: CanActivateFn = async (route, _state) => {
  const userSessionService = inject(UserSessionService);
  const authorizationStateService = inject(AuthorizationStateService);
  const organizationService = inject(OrganizationService);

  const requestedOrganizationId = resolveRequestedOrganizationId(route, authorizationStateService.authorizationState());
  if (requestedOrganizationId === undefined || requestedOrganizationId === userSessionService.activeOrganizationId()) {
    return true;
  }

  const response = await organizationService.getOrganizationById(requestedOrganizationId);
  if (response.success && response.data && response.data.status === OrganizationStatuses.Active) {
    userSessionService.activeOrganization.set(response.data);
    userSessionService.resetFilters();
  }

  return true;
};
